import "./style.scss";

import { getPendingImports } from "components/ImportData/utils";
import { useCompanySettings } from "hooks/useCompanySettings";
import { useCallback, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { fetchPeriods } from "store/periodStore";
import { useAppDispatch, useAppSelector } from "store/store";
import type { TImportTargets } from "types";

import ExportItem from "./ExportItem";
import { useExportConfigs } from "./useExportConfigs";

const ExportSettings = () => {
    const [t] = useTranslation();
    const dispatch = useAppDispatch();
    const { companySettings } = useAppSelector((state) => state.settings);
    const { periods, allPeriods, activePeriod } = useAppSelector((state) => state.period);
    const { jobs } = useAppSelector((state) => state.jobs);
    const { pendingImports } = useAppSelector((state) => state.dataImport);
    const { portalType } = useCompanySettings();

    // Determine if company uses stock management
    const usesStock = !!+companySettings?.settings?.find((sttg: any) => sttg.name == "uses_stock")?.content;

    // Fetch available periods on mount
    useEffect(() => {
        dispatch(fetchPeriods());
    }, [dispatch]);

    // Get export configurations for the current portal type
    const exportConfigs = useExportConfigs(usesStock);

    // Get list of pending import jobs
    const pendingJobs = getPendingImports({ jobs, fakeImports: pendingImports });

    const isLabelPending = useCallback(
        (label: string): boolean => {
            const pendingJobsType: TImportTargets[] = pendingJobs.map((item) => item.importType);
            switch (label) {
                case "Beneficiaries":
                    return pendingJobsType.includes("beneficiar");
                case "Titles":
                    return pendingJobsType.includes("title");
                case "Terms":
                    return pendingJobsType.includes("terms");
                case "Sales":
                    return pendingJobsType.includes("sales");
                default:
                    return false;
            }
        },
        [pendingJobs],
    );

    // Get the export configurations for the current portal type (with fallback to "book")
    const currentExports = exportConfigs[portalType ?? "book"];

    return (
        <div className="export-settings">
            <h1 className="export-settings-list-title">{t("Settings.Export")}</h1>
            {currentExports.map((config) => (
                <ExportItem
                    key={config.label}
                    config={config}
                    isPending={pendingJobs.length !== 0 && isLabelPending(config.label)}
                    periods={config.includeFuturePeriods ? allPeriods : periods}
                    defaultPeriod={config.defaultPeriod ?? activePeriod}
                />
            ))}
        </div>
    );
};

export default ExportSettings;
